// Lightweight GA4 + Microsoft Clarity wrapper.
// Both are env-gated: without VITE_GA_MEASUREMENT_ID / VITE_CLARITY_PROJECT_ID
// (set in .env.production only) every function is a safe no-op, so dev and
// staging traffic never pollutes production data.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;

const CONSENT_STORAGE_KEY = "fpc-consent-v1";

type ConsentChoice = "granted" | "denied";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

const isBrowser = typeof window !== "undefined";

export const analyticsConfigured = Boolean(GA_ID);

export function getStoredConsent(): ConsentChoice | null {
  if (!isBrowser) return null;
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

function loadClarity() {
  if (!isBrowser || !CLARITY_ID || window.clarity) return;
  window.clarity = function clarityStub(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((window.clarity as any).q = (window.clarity as any).q || []).push(args);
  };
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
  document.head.appendChild(script);
}

export function initAnalytics() {
  if (!isBrowser || !GA_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Google Consent Mode v2: everything denied until the visitor consents.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  // Preserve ad-click / campaign info in the URL while storage is denied so
  // sessions don't land in the "Unassigned" channel after consent.
  window.gtag("set", "url_passthrough", true);

  const stored = getStoredConsent();
  if (stored === "granted") {
    applyConsent(true);
  }

  window.gtag("js", new Date());
  // Pageviews are sent manually from the router (AnalyticsTracker) so SPA
  // navigations are counted exactly once.
  window.gtag("config", GA_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

function applyConsent(granted: boolean) {
  if (!isBrowser || typeof window.gtag !== "function") return;
  const value = granted ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
  if (granted) {
    loadClarity();
  }
}

export function setConsent(granted: boolean) {
  if (!isBrowser) return;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, granted ? "granted" : "denied");
  } catch {
    // Storage unavailable (private mode); consent still applies this session.
  }
  applyConsent(granted);
}

export function trackPageView(path: string) {
  if (!isBrowser || !GA_ID || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name: string, params?: Record<string, string | number | boolean>) {
  if (!isBrowser || !GA_ID || typeof window.gtag !== "function") return;
  window.gtag("event", name, params ?? {});
}

/** Buckets a probability (percent, 0-100) for the calculation_completed event. */
export function probabilityBucket(probabilityPercent: number): string {
  if (probabilityPercent < 0.1) return "<0.1%";
  if (probabilityPercent < 1) return "0.1-1%";
  if (probabilityPercent < 10) return "1-10%";
  return ">10%";
}
